import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface HangmanWrongEvent {
  letter: string;
  viewer: string;
  ts: number;
}

interface HangmanState {
  word: string;
  hint: string;
  category?: string;
  difficulty?: string;
  guessedLetters: string[];
  wrongLetters: string[];
  wrongGuesses: number;
  finished: boolean;
  winner: string | null;
  loser: boolean;
  lastWrong?: HangmanWrongEvent | null;
}

interface HangmanConfig {
  maxWrong: number;
  hintAfterWrong?: number;
}

/**
 * Hangman overlay — redesigned to match Hype Million / Chat Words gameshow
 * shell (stage spotlight, brand block + chips header, big rounded panels,
 * right-rail stats) but in violet/purple instead of gold/cyan so the three
 * read as a series.
 */

const PALETTE = {
  bg: '#0a0617',
  bgInner: '#1a0e2e',
  accent: '#a855f7', // purple-500
  accentLight: '#c4b5fd', // purple-300
  accentDeep: '#7c3aed', // purple-700
  accentBorder: 'rgba(168,85,247,0.55)',
  accentBorderSoft: 'rgba(168,85,247,0.35)',
  accentGlow: 'rgba(168,85,247,0.45)',
  green: '#22c55e',
  red: '#ef4444',
  amber: '#fbbf24',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.split('');

const ANIMATIONS = `
@keyframes hm-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes hm-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.55 } 100% { opacity: 0.3 } }
@keyframes hm-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes hm-rise { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes hm-sway { 0%, 100% { transform: rotate(-2deg) } 50% { transform: rotate(2deg) } }
@keyframes hm-part-drop { 0% { opacity: 0; transform: translateY(-12px) } 70% { opacity: 1; transform: translateY(2px) } 100% { transform: translateY(0) } }
@keyframes hm-tile-reveal { 0% { transform: rotateY(90deg); opacity: 0 } 100% { transform: rotateY(0); opacity: 1 } }
@keyframes hm-wrong-pop { 0% { transform: translate(-50%, -50%) scale(0.4) rotate(-15deg); opacity: 0 } 70% { transform: translate(-50%, -50%) scale(1.12) rotate(4deg); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1 } }
@keyframes hm-wrong-fade { 0% { opacity: 1 } 100% { opacity: 0; transform: translate(-50%, -50%) scale(1.15) } }
`;

export function HangmanBody({
  state,
  config,
}: {
  state: HangmanState;
  config: HangmanConfig | null;
}) {
  const maxWrong = config?.maxWrong ?? 6;
  const hintAfterWrong = config?.hintAfterWrong ?? 3;

  const prevWrongCountRef = useRef<number>(state.wrongGuesses);
  const prevGuessedCountRef = useRef<number>(state.guessedLetters.length);
  const prevWrongTsRef = useRef<number>(0);
  const prevFinishedRef = useRef<boolean>(false);
  const [wrongStamp, setWrongStamp] = useState<HangmanWrongEvent | null>(null);

  // Sound triggers — buzzer on wrong, ding on correct, end fanfare.
  useEffect(() => {
    if (state.wrongGuesses > prevWrongCountRef.current) {
      void gameSounds.play('incorrect');
    }
    prevWrongCountRef.current = state.wrongGuesses;
  }, [state.wrongGuesses]);
  useEffect(() => {
    if (state.guessedLetters.length > prevGuessedCountRef.current) {
      void gameSounds.play('correct');
    }
    prevGuessedCountRef.current = state.guessedLetters.length;
  }, [state.guessedLetters.length]);
  useEffect(() => {
    const lw = state.lastWrong;
    if (!lw) return;
    if (lw.ts <= prevWrongTsRef.current) return;
    prevWrongTsRef.current = lw.ts;
    setWrongStamp(lw);
    const t = setTimeout(() => setWrongStamp(null), 1300);
    return () => clearTimeout(t);
  }, [state.lastWrong]);
  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      if (state.winner) {
        void gameSounds.play('winner');
        void gameSounds.play('celebration');
      } else if (state.loser) {
        void gameSounds.play('loser');
      }
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished, state.winner, state.loser]);

  if (state.finished && state.winner) {
    return <Outcome win label="solved it" winner={state.winner} word={state.word} />;
  }
  if (state.finished && state.loser) {
    return <Outcome win={false} label="word was" winner="" word={state.word} />;
  }

  const showHint = state.wrongGuesses >= hintAfterWrong;
  const revealedTiles = state.word
    .split('')
    .map((c) =>
      c === ' ' ? ' ' : state.guessedLetters.includes(c.toLowerCase()) ? c.toUpperCase() : '_',
    );

  return (
    <div style={stageStyle}>
      <style>{ANIMATIONS}</style>
      <Spotlight />

      <Header
        category={state.category ?? ''}
        difficulty={state.difficulty ?? ''}
        wrongGuesses={state.wrongGuesses}
        maxWrong={maxWrong}
      />

      <main style={mainStyle}>
        <CommandStripe />
        <div style={playRowStyle}>
          <GallowsPanel wrongGuesses={state.wrongGuesses} maxWrong={maxWrong} />
          <WordPanel
            reveal={revealedTiles}
            wrongLetters={state.wrongLetters}
            guessedLetters={state.guessedLetters}
            hint={state.hint}
            showHint={showHint}
            hintAfterWrong={hintAfterWrong}
          />
        </div>
      </main>

      <StatsRail
        category={state.category ?? ''}
        difficulty={state.difficulty ?? ''}
        wrongGuesses={state.wrongGuesses}
        maxWrong={maxWrong}
        wrongLetters={state.wrongLetters}
        guessedLetters={state.guessedLetters}
      />

      {wrongStamp && <WrongStamp event={wrongStamp} />}
    </div>
  );
}

/* ───────────────────── stage / shell ───────────────────── */

const stageStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '1fr 320px',
  gridTemplateRows: 'auto 1fr',
  gap: 28,
  padding: '32px 40px',
  background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
  color: PALETTE.text,
  fontFamily:
    '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  overflow: 'hidden',
  animation: 'hm-fadeIn 400ms ease both',
};

const mainStyle: React.CSSProperties = {
  gridColumn: '1 / 2',
  gridRow: '2 / 3',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minHeight: 0,
};

const playRowStyle: React.CSSProperties = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '360px 1fr',
  gap: 24,
  minHeight: 0,
};

function Spotlight() {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(168,85,247,0.20) 0%, transparent 55%)',
          animation: 'hm-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 0% 100%, rgba(124,58,237,0.12) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/* ───────────────────── header ───────────────────── */

function Header({
  category,
  difficulty,
  wrongGuesses,
  maxWrong,
}: {
  category: string;
  difficulty: string;
  wrongGuesses: number;
  maxWrong: number;
}) {
  return (
    <header
      style={{
        gridColumn: '1 / -1',
        gridRow: '1 / 2',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        animation: 'hm-rise 500ms ease both',
      }}
    >
      <Brand />
      <ChipRow
        category={category}
        difficulty={difficulty}
        wrongGuesses={wrongGuesses}
        maxWrong={maxWrong}
      />
      <LivesRing wrongGuesses={wrongGuesses} maxWrong={maxWrong} />
    </header>
  );
}

function Brand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`,
          display: 'grid',
          placeItems: 'center',
          fontSize: 30,
          color: '#fff',
          fontWeight: 900,
          fontFamily: '"Orbitron",sans-serif',
          boxShadow: `0 8px 28px ${PALETTE.accentGlow}`,
        }}
      >
        ☠
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          The
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: 3,
            lineHeight: 1,
            fontFamily: '"Orbitron","Rajdhani",sans-serif',
          }}
        >
          HANGMAN
        </div>
      </div>
    </div>
  );
}

function ChipRow({
  category,
  difficulty,
  wrongGuesses,
  maxWrong,
}: {
  category: string;
  difficulty: string;
  wrongGuesses: number;
  maxWrong: number;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {category && <Chip label="Category" value={category} />}
      {difficulty && <Chip label="Difficulty" value={difficulty} highlight />}
      <Chip
        label="Wrong"
        value={`${wrongGuesses} / ${maxWrong}`}
        danger={wrongGuesses >= maxWrong - 1}
      />
    </div>
  );
}

function Chip({
  label,
  value,
  highlight = false,
  danger = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  danger?: boolean;
}) {
  const accent = danger ? PALETTE.red : highlight ? PALETTE.accentBorder : PALETTE.accentBorderSoft;
  const valueColor = danger ? '#fca5a5' : highlight ? PALETTE.accentLight : PALETTE.text;
  return (
    <div
      style={{
        padding: '10px 18px',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${accent}`,
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
        boxShadow:
          highlight || danger
            ? `0 0 24px -8px ${danger ? 'rgba(239,68,68,0.6)' : PALETTE.accentGlow}`
            : undefined,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: valueColor,
          fontFamily:
            highlight || danger ? '"JetBrains Mono","Orbitron",monospace' : '"Rajdhani",sans-serif',
          textTransform: highlight ? 'capitalize' : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* Lives ring — visual ring of skulls. The same role Hype Million's timer
 * fills, but reflecting wrongs remaining rather than seconds. */
function LivesRing({ wrongGuesses, maxWrong }: { wrongGuesses: number; maxWrong: number }) {
  const remaining = Math.max(0, maxWrong - wrongGuesses);
  const pct = Math.max(0, Math.min(1, remaining / maxWrong));
  const urgent = remaining <= 1;
  const r = 32;
  const c = 2 * Math.PI * r;
  return (
    <div
      style={{
        position: 'relative',
        width: 88,
        height: 88,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'hm-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={88}
        height={88}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle cx={44} cy={44} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={5} fill="none" />
        <circle
          cx={44}
          cy={44}
          r={r}
          stroke={urgent ? PALETTE.red : PALETTE.accent}
          strokeWidth={5}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms linear' }}
        />
      </svg>
      <div
        style={{
          fontSize: 26,
          fontWeight: 900,
          color: urgent ? PALETTE.red : PALETTE.accentLight,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {remaining}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: -16,
          fontSize: 9,
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        LIVES
      </div>
    </div>
  );
}

/* ───────────────────── command stripe ───────────────────── */

function CommandStripe() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '12px 18px',
        background:
          'linear-gradient(90deg, rgba(168,85,247,0.10), rgba(168,85,247,0.18), rgba(168,85,247,0.10))',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 600,
        color: PALETTE.textDim,
      }}
    >
      <span style={{ color: PALETTE.text }}>Just type</span>
      <span
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          padding: '4px 10px',
          background: 'rgba(34,197,94,0.18)',
          border: `1px solid ${PALETTE.green}`,
          borderRadius: 8,
          color: '#6ee7b7',
          fontWeight: 800,
          letterSpacing: 1,
        }}
      >
        a letter
      </span>
      <span>in chat — or</span>
      <span
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          padding: '4px 10px',
          background: 'rgba(168,85,247,0.18)',
          border: `1px solid ${PALETTE.accent}`,
          borderRadius: 8,
          color: PALETTE.accentLight,
          fontWeight: 800,
          letterSpacing: 1,
        }}
      >
        !solve &lt;word&gt;
      </span>
    </div>
  );
}

/* ───────────────────── gallows panel ───────────────────── */

function GallowsPanel({ wrongGuesses, maxWrong }: { wrongGuesses: number; maxWrong: number }) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        padding: '16px 18px',
        background: 'linear-gradient(180deg, rgba(26,14,46,0.85), rgba(10,6,23,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 22,
        boxShadow: '0 20px 56px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        minHeight: 0,
        animation: 'hm-rise 500ms ease both',
      }}
    >
      <Gallows wrongGuesses={wrongGuesses} maxWrong={maxWrong} />
    </div>
  );
}

function Gallows({ wrongGuesses, maxWrong }: { wrongGuesses: number; maxWrong: number }) {
  const step = (n: number) => wrongGuesses * (6 / Math.max(1, maxWrong)) >= n;
  return (
    <svg
      viewBox="0 0 400 540"
      style={{
        width: '100%',
        height: '100%',
        maxHeight: 460,
        color: PALETTE.accentLight,
        filter: `drop-shadow(0 0 32px ${PALETTE.accentGlow})`,
      }}
      fill="none"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <defs>
        <radialGradient id="hm-mist" cx="50%" cy="100%" r="80%">
          <stop offset="0%" stopColor="rgba(168, 85, 247, 0.35)" />
          <stop offset="60%" stopColor="rgba(168, 85, 247, 0.10)" />
          <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
        </radialGradient>
        <linearGradient id="hm-wood" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <filter id="hm-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <ellipse cx="200" cy="510" rx="220" ry="30" fill="url(#hm-mist)" />
      <line
        x1="40"
        y1="500"
        x2="360"
        y2="500"
        stroke="#a78bfa"
        strokeOpacity="0.7"
        strokeWidth="4"
      />
      <rect
        x="80"
        y="488"
        width="200"
        height="14"
        rx="3"
        fill="url(#hm-wood)"
        stroke="#5b21b6"
        strokeWidth="2"
      />
      <line x1="120" y1="488" x2="120" y2="60" stroke="url(#hm-wood)" strokeWidth="14" />
      <line x1="120" y1="160" x2="200" y2="60" stroke="url(#hm-wood)" strokeWidth="10" />
      <line x1="120" y1="60" x2="280" y2="60" stroke="url(#hm-wood)" strokeWidth="14" />
      <line
        x1="280"
        y1="60"
        x2="280"
        y2="130"
        stroke="#c4b5fd"
        strokeWidth="4"
        strokeDasharray="4 4"
        style={{ transformOrigin: '280px 60px', animation: 'hm-sway 4s ease-in-out infinite' }}
      />

      <g filter="url(#hm-glow)">
        {step(1) && (
          <circle
            cx="280"
            cy="170"
            r="34"
            stroke={PALETTE.amber}
            strokeWidth="6"
            fill="rgba(251, 191, 36, 0.1)"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
        {step(2) && (
          <line
            x1="280"
            y1="204"
            x2="280"
            y2="320"
            stroke={PALETTE.amber}
            strokeWidth="6"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
        {step(3) && (
          <line
            x1="280"
            y1="240"
            x2="232"
            y2="280"
            stroke={PALETTE.amber}
            strokeWidth="6"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
        {step(4) && (
          <line
            x1="280"
            y1="240"
            x2="328"
            y2="280"
            stroke={PALETTE.amber}
            strokeWidth="6"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
        {step(5) && (
          <line
            x1="280"
            y1="320"
            x2="240"
            y2="392"
            stroke={PALETTE.amber}
            strokeWidth="6"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
        {step(6) && (
          <line
            x1="280"
            y1="320"
            x2="320"
            y2="392"
            stroke={PALETTE.amber}
            strokeWidth="6"
            style={{ animation: 'hm-part-drop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          />
        )}
      </g>
    </svg>
  );
}

/* ───────────────────── word + hint + alphabet panel ───────────────────── */

function WordPanel({
  reveal,
  wrongLetters,
  guessedLetters,
  hint,
  showHint,
  hintAfterWrong,
}: {
  reveal: string[];
  wrongLetters: string[];
  guessedLetters: string[];
  hint: string;
  showHint: boolean;
  hintAfterWrong: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
      {/* WORD TILES */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 10,
          padding: '20px 12px',
          background: 'linear-gradient(180deg, rgba(26,14,46,0.85), rgba(10,6,23,0.95))',
          border: `2px solid ${PALETTE.accentBorderSoft}`,
          borderRadius: 18,
          boxShadow: '0 12px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
          animation: 'hm-rise 500ms ease both',
        }}
      >
        {reveal.map((c, i) => (
          <WordTile key={`${i}-${c}`} char={c} index={i} />
        ))}
      </div>

      {/* HINT */}
      <HintCard hint={hint} showHint={showHint} hintAfterWrong={hintAfterWrong} />

      {/* ALPHABET KEYBOARD */}
      <AlphabetKeyboard guessedLetters={guessedLetters} wrongLetters={wrongLetters} />
    </div>
  );
}

function WordTile({ char, index }: { char: string; index: number }) {
  const isSpace = char === ' ';
  const isBlank = char === '_';
  const isFilled = !isSpace && !isBlank;
  if (isSpace) {
    return <div style={{ width: 28 }} />;
  }
  return (
    <div
      style={{
        position: 'relative',
        width: 56,
        height: 72,
        display: 'grid',
        placeItems: 'center',
        background: isFilled
          ? `linear-gradient(180deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`
          : 'rgba(255,255,255,0.03)',
        border: isFilled ? `2px solid ${PALETTE.accentLight}` : '2px dashed rgba(255,255,255,0.18)',
        borderRadius: 12,
        boxShadow: isFilled
          ? `0 8px 24px ${PALETTE.accentGlow}, inset 0 1px 0 rgba(255,255,255,0.18)`
          : undefined,
        animation: isFilled
          ? `hm-tile-reveal 0.55s cubic-bezier(0.16,1,0.3,1) ${index * 50}ms both`
          : undefined,
        transformStyle: 'preserve-3d',
      }}
    >
      {isFilled && (
        <span
          style={{
            fontSize: 36,
            fontWeight: 900,
            fontFamily: '"Orbitron",sans-serif',
            color: '#fff',
            textShadow: '0 2px 6px rgba(0,0,0,0.6)',
          }}
        >
          {char}
        </span>
      )}
    </div>
  );
}

function HintCard({
  hint,
  showHint,
  hintAfterWrong,
}: {
  hint: string;
  showHint: boolean;
  hintAfterWrong: number;
}) {
  if (!showHint) {
    return (
      <div
        style={{
          padding: '14px 20px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px dashed rgba(255,255,255,0.12)',
          borderRadius: 14,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 3,
            color: PALETTE.textDim,
            fontFamily: '"Orbitron",sans-serif',
          }}
        >
          HINT LOCKED
        </div>
        <div
          style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.4)',
            fontStyle: 'italic',
            marginTop: 2,
          }}
        >
          Unlocks after {hintAfterWrong} wrong guess{hintAfterWrong === 1 ? '' : 'es'}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: '16px 22px',
        background: 'linear-gradient(90deg, rgba(168,85,247,0.18), rgba(168,85,247,0.04))',
        border: `1px solid ${PALETTE.accentBorder}`,
        borderRadius: 14,
        boxShadow: `0 0 32px -12px ${PALETTE.accentGlow}`,
        animation: 'hm-rise 400ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 3,
          color: PALETTE.accentLight,
          fontFamily: '"Orbitron",sans-serif',
          fontWeight: 800,
        }}
      >
        HINT
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: PALETTE.text,
          lineHeight: 1.3,
          marginTop: 2,
        }}
      >
        {hint}
      </div>
    </div>
  );
}

function AlphabetKeyboard({
  guessedLetters,
  wrongLetters,
}: {
  guessedLetters: string[];
  wrongLetters: string[];
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(13, 1fr)',
        gap: 6,
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 14,
      }}
    >
      {ALPHABET.map((l) => {
        const right = guessedLetters.includes(l);
        const wrong = wrongLetters.includes(l);
        const used = right || wrong;
        return (
          <div
            key={l}
            style={{
              padding: '8px 0',
              textAlign: 'center',
              fontFamily: '"JetBrains Mono",monospace',
              fontWeight: 900,
              fontSize: 14,
              borderRadius: 6,
              background: right
                ? 'rgba(34,197,94,0.22)'
                : wrong
                  ? 'rgba(239,68,68,0.22)'
                  : 'rgba(255,255,255,0.04)',
              border: right
                ? `1px solid ${PALETTE.green}`
                : wrong
                  ? `1px solid ${PALETTE.red}`
                  : '1px solid rgba(255,255,255,0.08)',
              color: right ? '#bbf7d0' : wrong ? '#fca5a5' : 'rgba(255,255,255,0.55)',
              opacity: used ? 1 : 0.65,
            }}
          >
            {l.toUpperCase()}
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────── right rail (stats) ───────────────────── */

function StatsRail({
  category,
  difficulty,
  wrongGuesses,
  maxWrong,
  wrongLetters,
  guessedLetters,
}: {
  category: string;
  difficulty: string;
  wrongGuesses: number;
  maxWrong: number;
  wrongLetters: string[];
  guessedLetters: string[];
}) {
  return (
    <aside
      style={{
        gridColumn: '2 / 3',
        gridRow: '2 / 3',
        position: 'relative',
        zIndex: 1,
        padding: '20px 18px',
        background: 'linear-gradient(180deg, rgba(26,14,46,0.85), rgba(10,6,23,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 22,
        boxShadow: '0 20px 56px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        animation: 'hm-rise 500ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 4,
          color: PALETTE.accentLight,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 800,
        }}
      >
        Round Stats
      </div>

      <StatRow label="Category" value={category || '—'} />
      <StatRow label="Difficulty" value={difficulty || '—'} capitalize />
      <StatRow label="Correct" value={String(guessedLetters.length)} accent={PALETTE.green} />
      {wrongGuesses >= maxWrong - 1 ? (
        <StatRow label="Wrong" value={`${wrongGuesses} / ${maxWrong}`} accent={PALETTE.red} />
      ) : (
        <StatRow label="Wrong" value={`${wrongGuesses} / ${maxWrong}`} />
      )}

      {/* Hearts row visualization */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 4 }}>
        {Array.from({ length: maxWrong }).map((_, i) => {
          const alive = i >= wrongGuesses;
          return (
            <span
              key={i}
              style={{
                fontSize: 24,
                opacity: alive ? 1 : 0.18,
                filter: alive ? 'drop-shadow(0 0 6px rgba(252,165,165,0.7))' : 'grayscale(1)',
                transition: 'opacity 300ms, filter 300ms',
              }}
            >
              {alive ? '🩸' : '☠'}
            </span>
          );
        })}
      </div>

      {wrongLetters.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: 3,
              color: PALETTE.textDim,
              fontFamily: '"Orbitron",sans-serif',
              marginBottom: 6,
            }}
          >
            WRONG LETTERS
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {wrongLetters.map((l) => (
              <span
                key={l}
                style={{
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: 'rgba(239,68,68,0.2)',
                  border: `1px solid rgba(239,68,68,0.5)`,
                  color: '#fca5a5',
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 900,
                  fontSize: 13,
                }}
              >
                {l.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

function StatRow({
  label,
  value,
  accent,
  capitalize = false,
}: {
  label: string;
  value: string;
  accent?: string;
  capitalize?: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: 2,
          color: PALETTE.textDim,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: accent ?? PALETTE.text,
          fontFamily: '"JetBrains Mono","Orbitron",monospace',
          textTransform: capitalize ? 'capitalize' : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ───────────────────── wrong stamp ───────────────────── */

function WrongStamp({ event }: { event: HangmanWrongEvent }) {
  return (
    <div
      key={event.ts}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 6,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
        animation:
          'hm-wrong-pop 0.35s cubic-bezier(0.16,1,0.3,1) both, hm-wrong-fade 0.3s ease-in 1s both',
      }}
    >
      <div
        style={{
          fontSize: 180,
          lineHeight: 1,
          fontWeight: 900,
          color: PALETTE.red,
          textShadow:
            '0 0 28px rgba(239, 68, 68, 0.95), 0 0 70px rgba(239, 68, 68, 0.45), 0 6px 18px rgba(0, 0, 0, 0.7)',
          WebkitTextStroke: '4px white',
        }}
      >
        ✕
      </div>
      <div
        style={{
          textAlign: 'center',
          background: 'rgba(0, 0, 0, 0.78)',
          padding: '6px 18px',
          borderRadius: 8,
          border: `2px solid ${PALETTE.red}`,
          boxShadow: '0 0 24px rgba(239, 68, 68, 0.55)',
        }}
      >
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 2,
            color: 'white',
          }}
        >
          &quot;{event.letter.toUpperCase()}&quot;
        </div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: '#fca5a5',
          }}
        >
          — {event.viewer}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── outcome (win / lose) ───────────────────── */

function Outcome({
  win,
  label,
  winner,
  word,
}: {
  win: boolean;
  label: string;
  winner: string;
  word: string;
}) {
  const accent = win ? PALETTE.green : PALETTE.red;
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: 40,
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily: '"Rajdhani","Orbitron",sans-serif',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          padding: '48px 60px',
          borderRadius: 24,
          background: 'rgba(0,0,0,0.4)',
          border: `2px solid ${accent}`,
          boxShadow: `0 0 100px -24px ${win ? 'rgba(34,197,94,0.6)' : 'rgba(239,68,68,0.6)'}`,
          textAlign: 'center',
          minWidth: 480,
          animation: 'hm-rise 500ms ease both',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 12 }}>{win ? '🏆' : '💀'}</div>
        <div
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 14,
            letterSpacing: 4,
            color: accent,
            textTransform: 'uppercase',
            fontWeight: 800,
          }}
        >
          {win ? 'Solved' : 'Out of guesses'}
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 36,
            fontWeight: 900,
            color: PALETTE.text,
            textShadow: `0 0 24px ${win ? 'rgba(34,197,94,0.7)' : 'rgba(239,68,68,0.7)'}`,
          }}
        >
          {winner ? `${winner} ${label}` : label}
        </div>
        <div
          style={{
            marginTop: 24,
            padding: '20px 32px',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 14,
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 48,
            fontWeight: 900,
            letterSpacing: 6,
            color: PALETTE.accentLight,
            textShadow: `0 0 28px ${PALETTE.accentGlow}`,
          }}
        >
          {word.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
